import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Ban, CheckCircle2, Trash2, Pencil, Copy, Coins, Ticket, ShieldCheck, History, ArrowDownAZ, Users, Minus, Plus, Crown, CalendarClock, RotateCcw } from "lucide-react";
import { api, formatError, avatarSrc, fmtDate, fmtDateTime, sgtToISO } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { AdminHeader, AdminModal, AField } from "../../components/AdminLayout";
import MilestoneTracker from "../../components/MilestoneTracker";
import EventDatePicker from "../../components/EventDatePicker";
import { MemberTicketFilter } from "../../components/admin/MemberTicketFilter";

const LEVEL_STEPS = [
  { value: "", label: "Auto (attendance based)" },
  { value: "beginner", label: "Beginner done → Intermediate unlocked" },
  { value: "intermediate", label: "Intermediate done → Advance unlocked" },
  { value: "advance", label: "Advance done → Focus unlocked" },
  { value: "focus", label: "Focus Group done" },
];

const ROLES = ["guest", "student", "vip", "instructor", "artist", "dj", "bartender", "staff", "admin", "super_admin"];
const ROLE_LABEL = { guest: "Guest", student: "Student", vip: "VIP member", instructor: "Instructor", artist: "Artist", dj: "DJ", bartender: "Bartender", staff: "Staff", admin: "Admin", super_admin: "Super admin" };
const ROLE_COLOR = { super_admin: "#D4AF37", admin: "#9D4EDD", artist: "#F59E0B", dj: "#22D3EE", bartender: "#F472B6", staff: "#38BDF8", instructor: "#5CC8FF", vip: "#EC4899", student: "#4ADE80", guest: "#A19DB0" };

const ReadRow = ({ label, value, tid }) => (
  <div>
    <label className="text-xs text-[#A19DB0]">{label}</label>
    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1">
      <span data-testid={tid} className="flex-1 text-sm truncate">{value || "—"}</span>
      {value && <button type="button" onClick={() => { navigator.clipboard?.writeText(value); toast.success("Copied"); }} data-testid={`${tid}-copy`} className="text-[#A19DB0] hover:text-white"><Copy size={14} /></button>}
    </div>
  </div>
);

export default function AdminMembers() {
  const { user } = useAuth();
  const isSuper = user.role === "super_admin";
  const canIssue = isSuper || user.can_issue_grants;
  const [members, setMembers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [roleFilter, setRoleFilter] = useState("all");
  const [sortAZ, setSortAZ] = useState(false);
  const [sel, setSel] = useState([]);
  const [dups, setDups] = useState(null);
  const [showDups, setShowDups] = useState(false);
  const [withPasses, setWithPasses] = useState(false);
  // issue sub-forms
  const [cAmt, setCAmt] = useState(""); const [cReason, setCReason] = useState(""); // eslint-disable-line no-unused-vars
  const [pPkg, setPPkg] = useState(""); const [pOpt, setPOpt] = useState(""); const [pReason, setPReason] = useState(""); const [pEventIds, setPEventIds] = useState([]); const [pExp, setPExp] = useState("");
  const [memberPasses, setMemberPasses] = useState([]);
  const [removedPasses, setRemovedPasses] = useState([]);
  const [usedPasses, setUsedPasses] = useState([]);
  const [expEdit, setExpEdit] = useState(null); const [expVal, setExpVal] = useState("");
  const [expMode, setExpMode] = useState("move"); const [moveEv, setMoveEv] = useState(""); const [allEvents, setAllEvents] = useState([]);
  useEffect(() => { if (expEdit && allEvents.length === 0) api.get("/admin/events").then((r) => setAllEvents(r.data || [])).catch(() => {}); }, [expEdit, allEvents.length]);
  const upcomingEvents = React.useMemo(() => { const now = new Date().toISOString(); return allEvents.filter((e) => (e.starts_at || "") >= now).sort((a, b) => (a.starts_at || "").localeCompare(b.starts_at || "")); }, [allEvents]);
  const [memberMs, setMemberMs] = useState(null);

  const [tGroups, setTGroups] = useState([]);
  const [tGroup, setTGroup] = useState("");
  const [tPkg, setTPkg] = useState("");
  const [tStatus, setTStatus] = useState("active");
  const ticketOn = !!(tGroup || tPkg);

  const load = useCallback(() => {
    const p = new URLSearchParams({ q, pass_status: tStatus });
    if (tPkg) p.set("package_id", tPkg); else if (tGroup) p.set("group", tGroup);
    api.get(`/admin/members?${p.toString()}`).then((r) => setMembers(r.data)).catch(() => {});
    api.get(`/admin/members/ticket-filters?pass_status=${tStatus}`).then((r) => setTGroups(r.data)).catch(() => {});
  }, [q, tGroup, tPkg, tStatus]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (canIssue) api.get("/admin/packages").then((r) => setPackages(r.data)).catch(() => {}); }, [canIssue]);

  const loadPasses = useCallback((mid) => api.get(`/admin/members/${mid}/passes`).then((r) => setMemberPasses(r.data)).catch(() => setMemberPasses([])), []);
  const loadRemoved = useCallback((mid) => {
    api.get(`/admin/members/${mid}/pass-history`).then((r) => setRemovedPasses(r.data)).catch(() => setRemovedPasses([]));
    api.get(`/admin/members/${mid}/used-passes`).then((r) => setUsedPasses(r.data)).catch(() => setUsedPasses([]));
  }, []);
  const uncheck = async (p) => {
    try {
      await api.post(`/admin/pass/${p.id}/undo-checkin`);
      toast.success("Check-in undone — 1 pass returned to member"); loadPasses(edit.id); loadRemoved(edit.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const loadMs = useCallback((mid) => api.get(`/admin/members/${mid}/milestones`).then((r) => setMemberMs(r.data)).catch(() => setMemberMs(null)), []);

  const saveExpiry = async (p) => {
    if (!expVal) { toast.error("Pick an expiry date"); return; }
    try {
      await api.post(`/admin/members/${edit.id}/update-expiry`, { pass_id: p.id, expires_at: sgtToISO(`${expVal}T23:59`) });
      toast.success("Expiry updated — pass now works at any event"); setExpEdit(null); setExpVal(""); loadPasses(edit.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const drinkAdjust = async (p, action) => {
    try {
      await api.post(`/admin/pass/${p.id}/${action}`);
      toast.success(action === "return-drink" ? "Drink coupon returned" : "Drink coupon marked as used"); loadPasses(edit.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const saveMove = async (p) => {
    if (!moveEv) { toast.error("Pick an event date"); return; }
    try {
      await api.post(`/admin/members/${edit.id}/move-pass-date`, { pass_id: p.id, event_id: moveEv });
      toast.success("Pass moved to the new date"); setExpEdit(null); setMoveEv(""); loadPasses(edit.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const markUsed = async (p) => {
    if (!window.confirm(`Mark "${p.package_name}" as used? It will move to history.`)) return;
    try {
      await api.post(`/admin/passes/${p.id}/use`);
      toast.success("Pass marked as used"); loadPasses(edit.id); loadRemoved(edit.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const openEdit = (m) => { setEdit({ ...m }); setCAmt(""); setCReason(""); setPPkg(""); setPOpt(""); setPReason(""); setPEventIds([]); setPExp(""); setMemberPasses([]); setRemovedPasses([]); setUsedPasses([]); setMemberMs(null); loadPasses(m.id); loadRemoved(m.id); loadMs(m.id); };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const payload = { role: edit.role };
      if (isSuper && edit.role === "admin") payload.can_issue_grants = !!edit.can_issue_grants;
      if (isSuper && edit.role === "admin") payload.can_edit_design = !!edit.can_edit_design;
      if (isSuper && edit.role === "vip") payload.vip_lifetime = edit.vip_lifetime === true;
      if (isSuper) payload.level_override = edit.level_override || null;
      payload.bio = edit.bio ?? "";
      await api.put(`/admin/members/${edit.id}`, payload);
      toast.success("Member updated"); setEdit(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const adjustCredit = async () => { // eslint-disable-line no-unused-vars
    if (!cAmt || !cReason.trim()) { toast.error("Enter an amount and a reason"); return; }
    try {
      const r = await api.post(`/admin/members/${edit.id}/adjust-credits`, { amount: parseInt(cAmt, 10), reason: cReason });
      setEdit((e) => ({ ...e, credits: r.data.credits })); setCAmt(""); setCReason("");
      toast.success("Passes updated"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const issuePass = async () => {
    if (!pPkg || !pReason.trim()) { toast.error("Choose a package and enter a reason"); return; }
    try {
      const r = await api.post(`/admin/members/${edit.id}/issue-pass`, { package_id: pPkg, option: pOpt || undefined, reason: pReason, event_ids: pEventIds, expires_at: pExp ? sgtToISO(`${pExp}T23:59`) : undefined });
      const n = (r.data?.event_ids || []).length;
      const pkgName = selPkg?.name ? `"${selPkg.name}" ` : "";
      toast.success(n > 0 ? `Pass issued — ${pkgName}linked to ${n} date${n !== 1 ? "s" : ""}, now counted on those scanners` : `Pass issued${pkgName ? ` — ${pkgName}` : ""}`);
      setPPkg(""); setPOpt(""); setPReason(""); setPEventIds([]); setPExp(""); loadPasses(edit.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const removePass = async (p) => {
    if (!window.confirm(`Remove "${p.package_name}" from ${edit.name}'s wallet? This cannot be undone.`)) return;
    const reason = window.prompt("Reason (optional) — shown to the member:", "") || "";
    try {
      await api.post(`/admin/members/${edit.id}/remove-pass`, { pass_id: p.id, reason });
      toast.success("Pass removed"); loadPasses(edit.id); loadRemoved(edit.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const toggleSuspend = async (m) => {
    try { await api.post(`/admin/members/${m.id}/suspend`, { suspended: !m.suspended }); toast.success(m.suspended ? "Reinstated" : "Suspended"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const del = async (m) => {
    if (!window.confirm(`Move ${m.name} to Deleted accounts? Their data is kept for 6 months and a super admin can restore it.`)) return;
    try { await api.delete(`/admin/members/${m.id}`); toast.success("Moved to Deleted accounts"); load(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const selPkg = packages.find((p) => p.id === pPkg);

  const roleCounts = React.useMemo(() => members.reduce((acc, m) => { acc[m.role] = (acc[m.role] || 0) + 1; return acc; }, {}), [members]);
  const shown = React.useMemo(() => {
    let list = roleFilter === "all" ? members : members.filter((m) => m.role === roleFilter);
    if (withPasses) list = list.filter((m) => (m.pass_count || 0) > 0);
    if (sortAZ) list = [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return list;
  }, [members, roleFilter, sortAZ, withPasses]);
  const toggleSel = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allShownSelected = shown.length > 0 && shown.every((m) => sel.includes(m.id));
  const toggleAll = () => setSel(allShownSelected ? sel.filter((id) => !shown.some((m) => m.id === id)) : [...new Set([...sel, ...shown.map((m) => m.id)])]);
  const bulkDel = async () => {
    if (sel.length === 0) return;
    if (!window.confirm(`Move ${sel.length} member${sel.length !== 1 ? "s" : ""} to Deleted accounts? Their data is kept for 6 months and a super admin can restore it.`)) return;
    try {
      const r = await api.post("/admin/members/bulk-delete", { ids: sel });
      toast.success(`Moved ${r.data.deleted} to Deleted accounts`);
      if (r.data.skipped?.length) toast.message(`Skipped: ${r.data.skipped.join(", ")}`);
      setSel([]); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const loadDups = async () => {
    const next = !showDups; setShowDups(next);
    if (next && dups === null) {
      const r = await api.get("/admin/duplicate-names").catch(() => null);
      setDups(r ? r.data : []);
    }
  };
  const resetTestAccounts = async () => {
    if (!window.confirm("Reset test accounts (Test 1, 2 & 3)? This wipes their passes, credits, bookings and orders and sets each back to a clean guest account. Friend links are kept.")) return;
    try {
      const r = await api.post("/admin/reset-test-accounts");
      toast.success(`Reset ${r.data.count} test account${r.data.count !== 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <AdminHeader title="Members" subtitle={`${shown.length} of ${members.length}`} />
      <div className="flex gap-2 mb-4" data-testid="members-subtabs">
        <span className="rounded-full px-4 py-1.5 text-sm font-semibold bg-[#D4AF37] text-black">Members</span>
        <Link to="/admin/deleted" data-testid="subtab-deleted" className="rounded-full px-4 py-1.5 text-sm font-medium bg-white/5 border border-white/10 text-[#A19DB0] hover:text-white">Deleted accounts</Link>
      </div>
      <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 mb-4 max-w-md">
        <Search size={18} className="text-[#A19DB0]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-member-search" placeholder="Search by name or email" className="bg-transparent outline-none flex-1 text-sm placeholder:text-white/30" />
      </div>

      <MemberTicketFilter groups={tGroups} group={tGroup} pkg={tPkg} status={tStatus}
        onGroup={(k) => { setTGroup(k); setTPkg(""); }} onPkg={setTPkg} onStatus={setTStatus}
        onClear={() => { setTGroup(""); setTPkg(""); }} />

      <div className="flex items-center gap-2 mb-3">
        <div className="flex gap-2 overflow-x-auto flex-1" data-testid="member-filters">
          <button onClick={() => setRoleFilter("all")} data-testid="member-filter-all" className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${roleFilter === "all" ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"}`}>All ({members.length})</button>
          {ROLES.filter((r) => roleCounts[r]).map((r) => (
            <button key={r} onClick={() => setRoleFilter(r)} data-testid={`member-filter-${r}`} className={`shrink-0 rounded-full px-3.5 py-1.5 text-sm font-medium border transition-colors ${roleFilter === r ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "bg-white/5 border-white/10 text-[#E8E4F0]"}`}>{ROLE_LABEL[r]} ({roleCounts[r]})</button>
          ))}
        </div>
        <button onClick={() => setSortAZ((v) => !v)} data-testid="member-sort-az" title="Sort A to Z" className={`shrink-0 flex items-center gap-1.5 text-sm rounded-full px-3 py-1.5 border transition-colors ${sortAZ ? "bg-white/15 border-white/20 text-white" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}><ArrowDownAZ size={15} /> A–Z</button>
      </div>

      {shown.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <label className="flex items-center gap-2 text-sm text-[#A19DB0]">
            <input type="checkbox" checked={allShownSelected} onChange={toggleAll} data-testid="member-select-all" className="accent-[#D4AF37] w-4 h-4" /> Select all
          </label>
          {sel.length > 0 && (
            <button onClick={bulkDel} data-testid="member-bulk-delete" className="flex items-center gap-1.5 text-sm font-semibold text-[#ffb4b4] bg-[#E57373]/10 border border-[#E57373]/30 rounded-full px-3 py-1.5">
              <Trash2 size={14} /> Delete ({sel.length})
            </button>
          )}
        </div>
      )}

      <div className="mb-4">
        <div className="flex items-center gap-4 flex-wrap">
          <button onClick={loadDups} data-testid="member-dups-toggle" className="flex items-center gap-1.5 text-sm text-[#A19DB0] hover:text-white">
            <Users size={15} /> Duplicate names {dups ? `(${dups.length})` : ""} {showDups ? "▲" : "▼"}
          </button>
          <button onClick={resetTestAccounts} data-testid="reset-test-accounts" className="flex items-center gap-1.5 text-sm text-[#A19DB0] hover:text-white">
            <RotateCcw size={15} /> Reset test accounts
          </button>
          <button onClick={() => setWithPasses((v) => !v)} data-testid="member-with-passes-toggle" className={`flex items-center gap-1.5 text-sm transition-colors ${withPasses ? "text-[#D4AF37]" : "text-[#A19DB0] hover:text-white"}`}>
            <Ticket size={15} /> With passes {withPasses ? `(${members.filter((m) => (m.pass_count || 0) > 0).length})` : ""}
          </button>
        </div>
        {showDups && (
          <div className="mt-2 rounded-xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="member-dups-panel">
            {dups === null ? <p className="text-[#A19DB0] text-xs">Checking…</p>
              : dups.length === 0 ? <p className="text-[#A19DB0] text-xs">No duplicate names — every account name is unique. ✓</p>
              : dups.map((g) => (
                <div key={g.name} className="text-xs">
                  <p className="text-[#E57373] font-medium">“{g.name}” — {g.count} accounts</p>
                  <div className="pl-3 mt-0.5 space-y-0.5">
                    {g.accounts.map((a) => <p key={a.id} className="text-[#A19DB0]">{a.email} · {a.role}</p>)}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {shown.map((m) => (
          <div key={m.id} data-testid={`member-row-${m.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl bg-white/5 border border-white/10 p-4">
            <input type="checkbox" checked={sel.includes(m.id)} onChange={() => toggleSel(m.id)} data-testid={`member-select-${m.id}`} className="accent-[#D4AF37] w-4 h-4 shrink-0" />
            <img src={avatarSrc(m)} alt="" className="w-11 h-11 rounded-full object-cover border border-white/10" />
            <div className="flex-1 min-w-[140px]">
              <div className="flex items-center gap-2">
                <p className="font-medium truncate">{m.name}</p>
                {m.suspended && <span className="text-[10px] uppercase bg-[#E57373]/20 text-[#E57373] px-2 py-0.5 rounded-full">Suspended</span>}
                {m.role === "vip" && m.vip_lifetime && (
                  <span data-testid={`vip-lifetime-badge-${m.id}`} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-[#D4AF37] text-black px-2 py-0.5 rounded-full shrink-0"><Crown size={11} /> VIP Lifetime</span>
                )}
              </div>
              <p className="text-[#A19DB0] text-xs truncate">{m.email}</p>
              <p className="text-[#A19DB0]/70 text-[11px] truncate" data-testid={`member-joined-${m.id}`}>Joined {fmtDate(m.join_date)}</p>
              {ticketOn && m.ticket_count > 0 && (
                <p data-testid={`member-ticket-info-${m.id}`} className="text-[#D4AF37] text-[11px] break-words">
                  {m.ticket_count} pass{m.ticket_count === 1 ? "" : "es"}{m.ticket_expiry ? ` · ${m.ticket_expired ? "expired" : "nearest expiry"} ${fmtDate(m.ticket_expiry)}` : ""}
                </p>
              )}
              {m.role === "vip" && m.vip_lifetime && m.vip_since && (
                <p data-testid={`vip-lifetime-since-${m.id}`} className="text-[#D4AF37] text-[11px] truncate">VIP Lifetime since {fmtDate(m.vip_since)}</p>
              )}
              {m.role === "vip" && !m.vip_lifetime && m.vip_expires_at && (() => {
                const days = Math.ceil((new Date(m.vip_expires_at).getTime() - Date.now()) / 86400000);
                const cls = days < 0 ? "text-[#E57373]" : days <= 14 ? "text-[#E5A84B]" : "text-[#A19DB0]";
                return <p data-testid={`vip-renewal-${m.id}`} className={`text-[11px] truncate ${cls}`}>{days < 0 ? `VIP expired ${fmtDate(m.vip_expires_at)}` : `VIP renews ${fmtDate(m.vip_expires_at)} · ${days} day${days === 1 ? "" : "s"} left`}</p>;
              })()}
            </div>
            <span className="text-xs px-2.5 py-1 rounded-full" style={{ background: `${ROLE_COLOR[m.role] || "#A19DB0"}22`, color: ROLE_COLOR[m.role] || "#A19DB0" }}>{ROLE_LABEL[m.role] || m.role}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => openEdit(m)} data-testid={`member-edit-${m.id}`} title="Edit" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><Pencil size={16} /></button>
              <button onClick={() => toggleSuspend(m)} data-testid={`member-suspend-${m.id}`} title={m.suspended ? "Reinstate" : "Suspend"} className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10">
                {m.suspended ? <CheckCircle2 size={16} className="text-[#9D4EDD]" /> : <Ban size={16} className="text-[#E57373]" />}
              </button>
              <button onClick={() => del(m)} data-testid={`member-delete-${m.id}`} title="Delete" className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10"><Trash2 size={16} className="text-[#E57373]" /></button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <AdminModal open onOpenChange={() => setEdit(null)} title={`Edit ${edit.name}`} onSubmit={saveEdit} busy={busy}>
          <div className="flex items-center gap-4">
            <img src={avatarSrc(edit)} alt="" data-testid="edit-photo" className="w-16 h-16 rounded-full object-cover border border-white/10" />
            <div className="text-sm"><p className="font-medium">{edit.name}</p><p className="text-[#A19DB0] text-xs">Joined {fmtDate(edit.join_date)} · {edit.credits} passes</p></div>
          </div>
          <ReadRow label="Name (read-only)" value={edit.name} tid="edit-name" />
          <ReadRow label="Phone (read-only)" value={edit.phone} tid="edit-phone" />

          {memberMs && (
            <div className="border-t border-white/10 pt-4 space-y-3">
              <MilestoneTracker data={memberMs} />
              {isSuper ? (() => {
                const idx = Math.max(0, LEVEL_STEPS.findIndex((o) => o.value === (edit.level_override || "")));
                const setIdx = (n) => { const i = Math.min(LEVEL_STEPS.length - 1, Math.max(0, n)); setEdit({ ...edit, level_override: LEVEL_STEPS[i].value }); };
                return (
                  <div data-testid="level-stepper">
                    <label className="text-xs text-[#A19DB0]">Completed level (unlocks the next level's classes & packages)</label>
                    <div className="flex items-center gap-3 mt-1">
                      <button type="button" onClick={() => setIdx(idx - 1)} disabled={idx <= 0} data-testid="level-minus" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center disabled:opacity-40 active:scale-90 transition-transform"><Minus size={16} /></button>
                      <div className="flex-1 text-center rounded-xl bg-white/5 border border-white/10 px-3 py-2">
                        <input type="number" min={0} max={4} value={idx} data-testid="level-number"
                          onChange={(e) => setIdx(parseInt(e.target.value, 10) || 0)}
                          className="w-full bg-transparent text-center text-lg font-display font-semibold outline-none" />
                        <p className="text-xs text-[#A19DB0]" data-testid="level-label">{LEVEL_STEPS[idx].label}</p>
                      </div>
                      <button type="button" onClick={() => setIdx(idx + 1)} disabled={idx >= LEVEL_STEPS.length - 1} data-testid="level-plus" className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center disabled:opacity-40 active:scale-90 transition-transform"><Plus size={16} /></button>
                    </div>
                    <p className="text-[11px] text-[#A19DB0]/70 mt-1.5">Auto = based on scanned attendance (3 classes per level). Marking a level as completed lets the student buy the next level up — e.g. Intermediate done → Advance unlocked. Press Save to confirm.</p>
                  </div>
                );
              })() : (
                <p className="text-xs text-[#A19DB0]">Current level: <span className="text-[#E8E4F0] font-medium">{memberMs.current_label}</span>. Only a super admin can set a member's dance level.</p>
              )}
            </div>
          )}
          <div>
            <label className="text-xs text-[#A19DB0]">Role</label>
            <select value={edit.role} onChange={(e) => setEdit({ ...edit, role: e.target.value })} data-testid="edit-role"
              disabled={!isSuper && edit.role === "super_admin"}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#D4AF37] disabled:opacity-50">
              {ROLES.map((r) => <option key={r} value={r} disabled={(r === "super_admin" || r === "vip") && !isSuper} className="bg-[#140F1E]">{ROLE_LABEL[r] || r}</option>)}
            </select>
          </div>

          {isSuper && edit.role === "vip" && (
            <div data-testid="vip-lifetime-block">
              <label className="text-xs text-[#A19DB0] flex items-center gap-1.5"><Crown size={13} className="text-[#EC4899]" /> VIP type</label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setEdit({ ...edit, vip_lifetime: false })} data-testid="vip-type-normal"
                  className={`rounded-xl px-3 py-2.5 text-sm font-medium border text-left ${!edit.vip_lifetime ? "bg-[#EC4899]/15 border-[#EC4899]/50 text-white" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}>
                  Normal<span className="block text-[11px] text-[#A19DB0]">6-month expiry · maintain 20 socials</span>
                </button>
                <button type="button" onClick={() => setEdit({ ...edit, vip_lifetime: true })} data-testid="vip-type-lifetime"
                  className={`rounded-xl px-3 py-2.5 text-sm font-medium border text-left ${edit.vip_lifetime ? "bg-[#D4AF37]/15 border-[#D4AF37]/50 text-white" : "bg-white/5 border-white/10 text-[#A19DB0]"}`}>
                  Lifetime<span className="block text-[11px] text-[#A19DB0]">Never expires · no maintenance</span>
                </button>
              </div>
              {edit.vip_lifetime && edit.vip_since && <p data-testid="vip-lifetime-since-modal" className="text-[#D4AF37] text-[11px] mt-2">VIP Lifetime since {fmtDate(edit.vip_since)}</p>}
            </div>
          )}

          <div>
            <label className="text-xs text-[#A19DB0]">Bio (shown on "Meet the team" for staff)</label>
            <textarea value={edit.bio || ""} onChange={(e) => setEdit({ ...edit, bio: e.target.value })} data-testid="edit-bio" rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#D4AF37] resize-none" placeholder="Short bio for instructors / artists" />
          </div>

          {isSuper && edit.role === "admin" && (
            <label className="flex items-center gap-2 text-sm rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
              <input type="checkbox" checked={!!edit.can_issue_grants} onChange={(e) => setEdit({ ...edit, can_issue_grants: e.target.checked })} data-testid="edit-can-issue" />
              <ShieldCheck size={15} className="text-[#D4AF37]" /> Allow this admin to issue credits &amp; passes
            </label>
          )}

          {isSuper && edit.role === "admin" && (
            <label className="flex items-center gap-2 text-sm rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
              <input type="checkbox" checked={!!edit.can_edit_design} onChange={(e) => setEdit({ ...edit, can_edit_design: e.target.checked })} data-testid="edit-can-design" />
              <ShieldCheck size={15} className="text-[#9D4EDD]" /> Allow this admin to edit the site design
            </label>
          )}

          <div className="border-t border-white/10 pt-4">
            <p className="font-display text-lg font-semibold flex items-center gap-2"><Coins size={17} className="text-[#D4AF37]" /> Passes</p>
            {!canIssue ? (
              <p className="text-[#A19DB0] text-sm mt-2">Only a super admin (or an admin granted the right) can issue credits or passes.</p>
            ) : (
              <div className="space-y-4 mt-3">
                <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2">
                  <p className="text-xs text-[#A19DB0] flex items-center gap-1.5"><Ticket size={13} className="text-[#D4AF37]" /> Issue a pass</p>
                  <select value={pPkg} onChange={(e) => { const np = e.target.value; setPPkg(np); setPOpt(""); const sp = packages.find((p) => p.id === np); setPEventIds(sp?.event_ids || []); }} data-testid="issue-pass-package" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]">
                    <option value="" className="bg-[#140F1E]">— choose a package —</option>
                    {packages.map((p) => <option key={p.id} value={p.id} className="bg-[#140F1E]">{p.name}</option>)}
                  </select>
                  {selPkg && (selPkg.options || []).length > 0 && (
                    <select value={pOpt} onChange={(e) => setPOpt(e.target.value)} data-testid="issue-pass-option" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]">
                      <option value="" className="bg-[#140F1E]">— choose an option —</option>
                      {selPkg.options.map((o) => <option key={o} value={o} className="bg-[#140F1E]">{o}</option>)}
                    </select>
                  )}
                  {selPkg && (selPkg.group !== "classes" || (selPkg.event_ids || []).length > 0) && (
                    <EventDatePicker value={pEventIds} onChange={setPEventIds} testidPrefix="issue-pass-events" />
                  )}
                  <AField label="Reason" value={pReason} onChange={(e) => setPReason(e.target.value)} data-testid="issue-pass-reason" placeholder="Comp / makeup class" />
                  <div>
                    <label className="text-xs text-[#A19DB0]">Expiry date (optional)</label>
                    <input type="date" value={pExp} onChange={(e) => setPExp(e.target.value)} data-testid="issue-pass-expiry" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 mt-1 text-sm outline-none focus:border-[#D4AF37]" />
                    <p className="text-[11px] text-[#A19DB0]/70 mt-1">Leave blank to use the package's default validity.</p>
                  </div>
                  <button type="button" onClick={issuePass} data-testid="apply-issue-pass" className="w-full rounded-full bg-[#D4AF37] text-black py-2 text-sm font-semibold active:scale-[0.98] transition-transform">Issue pass</button>
                </div>
                <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="member-passes">
                  <p className="text-xs text-[#A19DB0] flex items-center gap-1.5"><Ticket size={13} className="text-[#D4AF37]" /> Active passes ({memberPasses.length})</p>
                  {memberPasses.length === 0 ? (
                    <p className="text-[#A19DB0]/70 text-xs">No active passes.</p>
                  ) : memberPasses.map((p) => (
                    <div key={p.id} data-testid={`member-pass-${p.id}`} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="w-1.5 h-8 rounded-full shrink-0" style={{ background: p.color || "#9D4EDD" }} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm truncate">{p.package_name}{p.bundle_size > 1 ? ` · Pass ${p.bundle_index} of ${p.bundle_size}` : ""}{p.option ? ` · ${p.option}` : ""}</p>
                          <p className="text-[#A19DB0] text-[11px]">{p.group}{p.group === "classes" ? ` · ${p.credits_left}/${p.credits_total} left` : ""} · exp {fmtDate(p.expires_at)}</p>
                          {p.drink_total > 0 && (
                            <div className="flex items-center gap-2 mt-1" data-testid={`member-pass-drinks-${p.id}`}>
                              <span className="text-[11px] text-[#F472B6]">Drinks {p.drink_left}/{p.drink_total}</span>
                              {isSuper && <>
                                <button type="button" disabled={p.drink_left <= 0} onClick={() => drinkAdjust(p, "redeem-drink")} data-testid={`drink-mark-${p.id}`} className="whitespace-nowrap rounded-full bg-white/5 border border-white/10 px-2 py-0.5 text-[10px] disabled:opacity-40">Mark used</button>
                                <button type="button" disabled={p.drink_left >= p.drink_total} onClick={() => drinkAdjust(p, "return-drink")} data-testid={`drink-return-${p.id}`} className="rounded-full bg-[#D4AF37]/15 text-[#D4AF37] px-2 py-0.5 text-[10px] disabled:opacity-40">Return</button>
                              </>}
                            </div>
                          )}
                        </div>
                        {canIssue && (
                          <button type="button" onClick={() => { setExpEdit(expEdit === p.id ? null : p.id); setExpVal((p.expires_at || "").slice(0, 10)); setExpMode(p.is_class_credit ? "expiry" : "move"); setMoveEv(""); }} data-testid={`edit-expiry-${p.id}`} title="Edit date / expiry" className="w-8 h-8 rounded-full bg-[#D4AF37]/15 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37]/25 shrink-0"><CalendarClock size={15} /></button>
                        )}
                        {isSuper && p.group === "socials" && (
                          <button type="button" onClick={() => markUsed(p)} data-testid={`use-pass-${p.id}`} title="Mark as used" className="w-8 h-8 rounded-full bg-[#9D4EDD]/15 text-[#c79bff] flex items-center justify-center hover:bg-[#9D4EDD]/25 shrink-0"><CheckCircle2 size={15} /></button>
                        )}
                        {isSuper && !p.is_class_credit && (
                          <button type="button" onClick={() => removePass(p)} data-testid={`remove-pass-${p.id}`} title="Remove pass" className="w-8 h-8 rounded-full bg-[#E57373]/15 text-[#E57373] flex items-center justify-center hover:bg-[#E57373]/25 shrink-0"><Trash2 size={15} /></button>
                        )}
                      </div>
                      {expEdit === p.id && (
                        <div className="mt-2 space-y-2" data-testid={`expiry-editor-${p.id}`}>
                          {!p.is_class_credit && (
                            <div className="flex gap-1 bg-white/5 rounded-full p-0.5 w-fit">
                              {[["move", "Move to another date"], ["expiry", "Change expiry"]].map(([k, l]) => (
                                <button key={k} type="button" onClick={() => setExpMode(k)} data-testid={`date-mode-${k}-${p.id}`} className={`rounded-full px-3 py-1 text-xs transition-colors ${expMode === k ? "bg-[#D4AF37] text-black font-semibold" : "text-[#A19DB0]"}`}>{l}</button>
                              ))}
                            </div>
                          )}
                          {expMode === "move" && !p.is_class_credit ? (
                            <div className="flex flex-wrap items-center gap-2 min-w-0">
                              <select value={moveEv} onChange={(e) => setMoveEv(e.target.value)} data-testid={`move-event-select-${p.id}`} className="w-full min-w-0 max-w-full truncate bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37]">
                                <option value="" className="bg-[#140F1E]">— pick the new event date —</option>
                                {upcomingEvents.map((ev) => <option key={ev.id} value={ev.id} className="bg-[#140F1E]">{ev.title} · {fmtDateTime(ev.starts_at)}</option>)}
                              </select>
                              <button type="button" onClick={() => saveMove(p)} data-testid={`save-move-${p.id}`} className="rounded-full bg-[#D4AF37] text-black px-3 py-1.5 text-xs font-semibold shrink-0">Save</button>
                              <button type="button" onClick={() => setExpEdit(null)} className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs shrink-0">Cancel</button>
                            </div>
                          ) : (
                        <div className="flex items-center gap-2">
                          <input type="date" value={expVal} onChange={(e) => setExpVal(e.target.value)} data-testid={`expiry-input-${p.id}`} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-[#D4AF37]" />
                          <button type="button" onClick={() => saveExpiry(p)} data-testid={`save-expiry-${p.id}`} className="rounded-full bg-[#D4AF37] text-black px-3 py-1.5 text-xs font-semibold shrink-0">Save</button>
                          <button type="button" onClick={() => { setExpEdit(null); setExpVal(""); }} className="rounded-full bg-white/5 border border-white/10 px-3 py-1.5 text-xs shrink-0">Cancel</button>
                        </div>
                          )}
                          {expMode === "expiry" && !p.is_class_credit && <p className="text-[11px] text-[#A19DB0]">The pass will work at any event until this date.</p>}
                        </div>
                      )}
                    </div>
                  ))}
                  {!isSuper && memberPasses.length > 0 && <p className="text-[#A19DB0]/60 text-[11px]">Only a super admin can remove passes.</p>}
                </div>
                {usedPasses.length > 0 && (
                  <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="used-passes">
                    <p className="text-xs text-[#A19DB0] flex items-center gap-1.5"><History size={13} className="text-[#4ADE80]" /> Checked-in passes ({usedPasses.length})</p>
                    {usedPasses.map((p) => (
                      <div key={p.id} data-testid={`used-pass-${p.id}`} className="flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-xs">
                        <div className="flex-1 min-w-0">
                          <p className="text-[#E8E4F0] truncate">{p.package_name}{p.bundle_size > 1 ? ` · Pass ${p.bundle_index} of ${p.bundle_size}` : ""}</p>
                          <p className="text-[#A19DB0] mt-0.5">Checked in {fmtDateTime(p.used_at || p.checked_in_at)}</p>
                        </div>
                        <button type="button" onClick={() => uncheck(p)} data-testid={`uncheck-pass-${p.id}`} className="rounded-full bg-[#D4AF37]/15 text-[#D4AF37] px-3 py-1 text-[11px] font-semibold shrink-0">Uncheck</button>
                      </div>
                    ))}
                  </div>
                )}
                {removedPasses.length > 0 && (
                  <div className="rounded-xl bg-white/5 border border-white/10 p-3 space-y-2" data-testid="removed-passes">
                    <p className="text-xs text-[#A19DB0] flex items-center gap-1.5"><History size={13} className="text-[#E57373]" /> Removed passes ({removedPasses.length})</p>
                    {removedPasses.map((g) => (
                      <div key={g.id} data-testid={`removed-pass-${g.id}`} className="rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-xs">
                        <p className="text-[#E8E4F0] line-through decoration-[#E57373]/60">{g.package_name || "Pass"}</p>
                        <p className="text-[#A19DB0] mt-0.5">by {g.admin_name || "an admin"} · {fmtDateTime(g.created_at)}</p>
                        {g.reason && <p className="text-[#A19DB0]/80 mt-0.5 italic">“{g.reason}”</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </AdminModal>
      )}
    </div>
  );
}
